import React, { FC, useEffect, useRef, useState } from "react";
import { Loader } from "shared/ui";
import dayjs from "dayjs";
import {
  OrderProcessInfoBox,
  OrderStepTrackerResponseItem,
  useGetTasksExecutionQuery,
  useStartWorkingOnProcessMutation,
} from "shared/api";
import { useModal } from "shared/hooks";
import { ProducedItemsInOrdersModal } from "./ProducedItemsInOrdersModal";
import { ProdPageHeader } from "./ProdPageHeader";
import { ProdPageContent } from "./ProdPageContent";
import { MaintenanceTable } from "../MaintenanceTable";

interface DynamicProcessStepsForTheOrderProps {
  processStepId?: number;
  orderId?: string;
  orderProcessStepInfoBox?: OrderProcessInfoBox;
  incrementProcessStepIndex: () => void;
  decrementProcessStepIndex: () => void;
  orderStepTrackerResponseItem?: OrderStepTrackerResponseItem;
  isLoadingOrderData: boolean;
  clearSelectedOrder: () => void;
  refetchOrderProcessStepTracker: () => void;
  isLoadingOrderCodes: boolean;
  navigateOrder: (direction: "next" | "previous") => void;
  productCardId: number;
}

const DynamicProcessStepsForTheOrder: FC<
  DynamicProcessStepsForTheOrderProps
> = ({
  processStepId,
  orderId,
  incrementProcessStepIndex,
  decrementProcessStepIndex,
  orderStepTrackerResponseItem,
  isLoadingOrderData,
  clearSelectedOrder,
  refetchOrderProcessStepTracker,
  isLoadingOrderCodes,
  navigateOrder,
  orderProcessStepInfoBox,
  productCardId,
}) => {
  const [locked, setLocked] = useState(true);
  const [startWorkingOnProcess] = useStartWorkingOnProcessMutation();

  const startProcessAndUnlock = async (id: number) => {
    const payload = {
      machine: orderStepTrackerResponseItem?.machine || null,
      start_datetime: orderStepTrackerResponseItem?.start_datetime || null,
      end_datetime: orderStepTrackerResponseItem?.end_datetime || null,
      status: orderStepTrackerResponseItem?.status || null,
      info: orderStepTrackerResponseItem?.info ?? "",
    };

    try {
      await startWorkingOnProcess({ id, data: payload }).unwrap();
      setLocked(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to start process:", error);
    }
  };

  const handleLocked = () => {
    const id = orderStepTrackerResponseItem?.id;
    if (typeof id === "number") {
      startProcessAndUnlock(id);
    } else {
      // eslint-disable-next-line no-console
      console.error("ID is undefined. Cannot start process.");
    }
  };

  const [
    producedItemInOrderModal,
    openProducedItemInOrderModal,
    hideProducedItemInOrderModal,
  ] = useModal();

  const closeProdPageModal = () => {
    hideProducedItemInOrderModal();
    clearSelectedOrder();
  };

  const [showMaintenanceIcon, setShowMaintenanceIcon] = useState(false);
  const [showMaintenanceTable, setShowMaintenanceTable] = useState(false);
  const tableRef = useRef<HTMLDivElement>(null);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [countOfTasksExecutions, setCountOfTasksExecutions] = useState(0);

  const handleshowMaintenanceTableState = (value: boolean) => {
    setShowMaintenanceTable(value);
  };

  const handleshowMaintenanceIconState = (value: boolean) => {
    setShowMaintenanceIcon(value);
  };

  const now = dayjs().format("YYYY-MM-DDTHH:mm:ss");
  const {
    data: tasksExecutionData,
    isLoading,
    isFetching,
  } = useGetTasksExecutionQuery({
    user__username: localStorage.getItem("username") ?? "",
    timestamp_expected__lte: now,
    status: "PENDING",
    expand: "task,user",
    ordering: "-status,timestamp_expected",
  });

  const tasksExecution = tasksExecutionData?.results ?? [];

  useEffect(() => {
    if (tasksExecutionData) {
      setCountOfTasksExecutions(
        tasksExecutionData.count ?? tasksExecution.length
      );
    }
  });

  return (
    <>
      <ProdPageHeader
        countOfTasksExecutions={countOfTasksExecutions}
        productCardId={productCardId}
        showMaintenanceIcon={showMaintenanceIcon}
        handleshowMaintenanceTableState={handleshowMaintenanceTableState}
        incrementProcessStepIndex={incrementProcessStepIndex}
        decrementProcessStepIndex={decrementProcessStepIndex}
        orderStepTrackerResponseItem={orderStepTrackerResponseItem}
        closeProdPageModal={closeProdPageModal}
        isLoadingOrderCodes={isLoadingOrderCodes}
        navigateOrder={navigateOrder}
      />

      {!isLoadingOrderData ? (
        <ProdPageContent
          setShowMaintenanceTable={setShowMaintenanceTable}
          handleLocked={handleLocked}
          locked={locked}
          handleshowMaintenanceIconState={handleshowMaintenanceIconState}
          showMaintenanceTable={showMaintenanceTable}
          orderStepTrackerResponseItem={orderStepTrackerResponseItem}
          orderProcessStepInfoBox={orderProcessStepInfoBox}
          processStepId={processStepId}
          orderId={orderId}
          openProducedItemInOrderModal={openProducedItemInOrderModal}
          refetchOrderProcessStepTracker={refetchOrderProcessStepTracker}
          tableRef={tableRef}
          statusModalOpen={statusModalOpen}
          setStatusModalOpen={setStatusModalOpen}
        />
      ) : (
        <Loader />
      )}

      {producedItemInOrderModal && (
        <ProducedItemsInOrdersModal
          isOpenModal={producedItemInOrderModal}
          hideProducedItemInOrderModal={hideProducedItemInOrderModal}
          processStepId={processStepId}
        />
      )}

      {showMaintenanceTable && (
        <div
          ref={tableRef}
          className="fixed top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 w-full max-w-7xl z-[100]"
        >
          <MaintenanceTable
            tasksExecution={tasksExecution}
            isLoading={isLoading}
            isFetching={isFetching}
            statusModalOpen={statusModalOpen}
            setStatusModalOpen={setStatusModalOpen}
            isMessageModalOpen={isMessageModalOpen}
            setIsMessageModalOpen={setIsMessageModalOpen}
          />
        </div>
      )}
    </>
  );
};

export { DynamicProcessStepsForTheOrder };
